import React, { Component } from 'react'
import { withFormik } from 'formik'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import TextInput from '@app/components/ui/inputs/TextInput'
import RadioGroup from '@app/components/ui/inputs/RadioGroup'
import RadioButton from '@app/components/ui/inputs/RadioButton'
import Submit from '@app/components/ui/inputs/Submit'
import SelectInput from '@app/components/ui/selects/SelectInput'
import ActionButton from '@app/components/ui/buttons/ActionButton'
import * as Yup from '@app/helpers/yup'
import _ from 'lodash'

const formikEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    name: Yup.string().required(),

    surname: Yup.string().required(),

    gender: Yup.string().oneOf(['1', '2']).required(),

    login: Yup.string()
      .when('id', (id, schema) => {
        return id ? schema.required() : schema
      }),

    grade: Yup.string()
      .when('mode', (mode, schema) => {
        return mode == "parent" ? schema.required() : schema
      }),

    password: Yup.string().nullable()
      .when('id', (id, schema) => {
        return id ? schema.required() : schema
      })
  }),

  mapPropsToValues: ({ student: { object }, config: { mode } }) => ({
    id: object.id,
    mode: mode,
    name: object.name || '',
    surname: object.surname || '',
    gender: object.gender || '',
    login: object.login || '',
    grade: object.grade || '',
    password: object.password || ''
  }),

  handleSubmit: (payload, { props }) => {
    const { createStudentAction, updateStudentAction } = props

    if (!payload.id) createStudentAction(payload)
    else updateStudentAction(payload)
  },

  displayName: 'StudentsForm'
});

class Edit extends Component {
  constructor(props) {
    super(props)

    this.handleCancel = this.handleCancel.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.student.processing && !this.props.student.processing) {
      let {
        setSubmitting,
        onSave,
        student: { error, validationErrors }
      } = this.props

      setSubmitting(false)

      if (!error && !validationErrors) {
        if (onSave) { onSave(); }
      }
    }
  }

  handleCancel(e) {
    const { onCancel } = this.props

    if (onCancel) { return onCancel(e) }
  }

  handleDelete(e) {
    const {
      student: { object },
      setSubmitting,
      deleteStudentAction
    } = this.props

    setSubmitting(true)
    deleteStudentAction(object.id)
  }

  render() {
    const {
      values,
      touched,
      errors,
      handleChange,
      handleBlur,
      setFieldValue,
      isSubmitting,
      submitCount,
      submitForm,
      number,
      config,
      global: { olympiads },
      student,
      handleSubmit
    } = this.props;

    const persisted = !!values.id
    const remoteErrors = student.validationErrors || {}

    const isEventActive = config.event && !config.event.beforeTrialTour && !config.event.afterMainTour

    return (
      <form onSubmit={handleSubmit}>
        <TextInput
          type="text"
          id={`${number}_name`}
          name="name"
          className="students-table__text-input"
          disabled={isSubmitting}
          readOnly={isSubmitting}
          error={touched.name && errors.name || remoteErrors.name}
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={I18n.t("js.students_table.name")}
          label={I18n.t("js.students_table.name")}
        />

        <TextInput
          type="text"
          id={`${number}_surname`}
          name="surname"
          className="students-table__text-input"
          disabled={isSubmitting}
          readOnly={isSubmitting}
          error={touched.surname && errors.surname || remoteErrors.surname}
          value={values.surname}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={I18n.t("js.students_table.surname")}
          label={I18n.t("js.students_table.surname")}
        />

        <RadioGroup classic error={touched.gender && errors.gender || remoteErrors.gender}>
          <RadioButton
            id={`${number}_gender_male`}
            name={`${number}_gender`}
            checked={values.gender == 1}
            value={1}
            label={ I18n.t('gender.male_long') }
            disabled={isSubmitting}
            readOnly={isSubmitting}
            onChange={ (e) => setFieldValue('gender', e.target.value) }
            onBlur={handleBlur}
          />
          <RadioButton
            id={`${number}_gender_female`}
            name={`${number}_gender`}
            checked={values.gender == 2}
            value={2}
            label={ I18n.t('gender.female_long') }
            disabled={isSubmitting}
            readOnly={isSubmitting}
            onChange={ (e) => setFieldValue('gender', e.target.value) }
            onBlur={handleBlur}
          />
        </RadioGroup>

        { config.mode === 'parent' ? (
            <SelectInput
              id={`${number}_grade`}
              name="grade"
              isSearchable={false}
              isDisabled={isSubmitting}
              readOnly={isSubmitting}
              error={touched.grade && errors.grade || remoteErrors.grade}
              value={ _.find(olympiads, ['grade', values.grade]) }
              onChange={ (option) => setFieldValue('grade', option.grade) }
              onBlur={handleBlur}
              options={olympiads}
              placeholder={I18n.t("js.students_table.grade")}
              label={I18n.t("js.students_table.grade")}
              getOptionLabel={(option) => option.programme}
              getOptionValue={(option) => option.id}
            />
          ) : null
        }

        { persisted ? (
            <TextInput
              type="text"
              id={`${number}_login`}
              name="login"
              className="students-table__text-input"
              disabled={isSubmitting}
              readOnly={isSubmitting}
              error={touched.login && errors.login || remoteErrors.login}
              value={values.login}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={I18n.t("js.students_table.login")}
              label={I18n.t("js.students_table.login")}
              hint={I18n.t("registration.password_hint")}
            />
          ) : null
        }

        { persisted ? (
            <TextInput
              type="text"
              id="password"
              className="students-table__text-input"
              disabled={isSubmitting}
              readOnly={isSubmitting}
              error={touched.password && errors.password || remoteErrors.password}
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={I18n.t("js.students_table.password")}
              label={I18n.t("js.students_table.password")}
              hint={I18n.t("registration.password_hint")}
            />
          ) : null
        }

        <div className="w-flex-layout w-flex-layout--columns mt-2">
          <ActionButton
             className="btn f--upper"
             disabled={submitCount > 0 && !_.isEmpty(errors) || isSubmitting}
             loading={isSubmitting}
             onClick={(e) => submitForm()}>
            { I18n.t("common.save") }
          </ActionButton>

          { config.deleteEnabled && persisted ? (
              <button
                className="btn btn--light-blue f--upper"
                onClick={this.handleDelete}
                disabled={isSubmitting}>

                { config.mode === 'parent' ?
                    I18n.t("js.students_table.delete_child") :
                    I18n.t("js.students_table.delete")
                }
              </button>
            ) : null
          }
        </div>
      </form>
    )
  }
}

Edit.defaultProps = {}

Edit.propTypes = {
  student: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  global: PropTypes.object.isRequired,
  createStudentAction: PropTypes.func.isRequired,
  updateStudentAction: PropTypes.func.isRequired,
  deleteStudentAction: PropTypes.func.isRequired
}

export default formikEnhancer(Edit)
